export interface CheckoffOperationLike {
  operationId?: string;
  queuedAt: number;
}

import {
  compareCheckoffOperationFreshness,
  parseCheckoffOperationId,
  type CheckoffOperationFreshness,
} from "@batch-and-bowl/shared";
import type { ServerConfirmedCheckoff } from "./grocery-checkoff-confirmed";
import {
  clearExactPolicyRecoveryMarker,
  clearExactProcessPolicyRecoveryMarker,
  isGeneratorSafeSource,
  loadProcessPolicyRecoveryMarker,
  matchesExactProcessPolicyRecoveryMarker,
  readPolicyRecoveryMarker,
  shouldRetainPolicyRecoveryMarker,
  type CheckoffGeneratorSafetySource,
  type CheckoffPolicyRecoveryMarkerV1,
} from "./grocery-checkoff-recovery";

export {
  compareCheckoffOperationFreshness,
  parseCheckoffOperationId,
  type CheckoffOperationFreshness,
};

export const CHECKOFF_OPERATION_CLOCK_STORAGE_KEY_V3 =
  "bb.checkoff-operation-clock.v3";
export const CHECKOFF_OPERATION_CLOCK_STORAGE_PREFIX_V4 =
  "bb.checkoff-operation-clock.v4:";
const LEGACY_CLOCK_KEY = "bb.checkoff-operation-clock.v1";
const MAX_SUPPORTED_PHYSICAL_TIME = 8_640_000_000_000_000;
const CANONICAL_DECIMAL = /^(?:0|[1-9][0-9]*)$/;

type ClockStorage = Partial<
  Pick<Storage, "length" | "key" | "getItem" | "setItem" | "removeItem">
>;
let sequence = "0";
let instanceId: string | undefined;
let processClock: CheckoffOperationFreshness | undefined;

export interface CheckoffOperationClockRecordV4 {
  operationId: string;
  freshness: CheckoffOperationFreshness;
}

export function compareCanonicalDecimal(left: string, right: string): number {
  if (left.length !== right.length) return left.length < right.length ? -1 : 1;
  return left === right ? 0 : left < right ? -1 : 1;
}

export function incrementCanonicalDecimal(value: string): string {
  const digits = value.split("");
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    if (digits[index] !== "9") {
      digits[index] = String(Number(digits[index]) + 1);
      return digits.join("");
    }
    digits[index] = "0";
  }
  return `1${digits.join("")}`;
}

function validPhysical(value: string): boolean {
  if (!CANONICAL_DECIMAL.test(value)) return false;
  const numeric = Number(value);
  return (
    Number.isSafeInteger(numeric) && numeric <= MAX_SUPPORTED_PHYSICAL_TIME
  );
}

export function checkoffOperationClockRecordStorageKey(
  operationId: string,
): string {
  return `${CHECKOFF_OPERATION_CLOCK_STORAGE_PREFIX_V4}${encodeURIComponent(
    operationId,
  )}`;
}

export function parseCheckoffOperationClockRecord(
  key: string | null,
  raw: string | null,
): CheckoffOperationClockRecordV4 | undefined {
  if (
    !key?.startsWith(CHECKOFF_OPERATION_CLOCK_STORAGE_PREFIX_V4) ||
    raw === null
  ) {
    return undefined;
  }
  const suffix = key.slice(CHECKOFF_OPERATION_CLOCK_STORAGE_PREFIX_V4.length);
  let decoded: string;
  try {
    decoded = decodeURIComponent(suffix);
  } catch {
    return undefined;
  }
  if (encodeURIComponent(decoded) !== suffix || decoded !== raw) {
    return undefined;
  }
  const freshness = parseCheckoffOperationId(decoded);
  if (!freshness || freshness.version !== 3) return undefined;
  return { operationId: decoded, freshness };
}

function freshnessOf(entry: CheckoffOperationLike): CheckoffOperationFreshness {
  const parsed = parseCheckoffOperationId(entry.operationId);
  if (parsed) return parsed;
  return {
    physicalTime: validPhysical(String(entry.queuedAt)) ? entry.queuedAt : 0,
    logicalTime: "0",
    instanceId: `legacy-${entry.operationId ?? "none"}`,
    sequence: "0",
    version: 2,
  };
}

interface StorageKeySnapshot {
  keys: string[];
  complete: boolean;
}

function snapshotStorageKeys(
  storage: ClockStorage | null | undefined,
): StorageKeySnapshot {
  if (!storage) return { keys: [], complete: false };
  let length: number;
  let keyAt: Storage["key"] | undefined;
  try {
    length = storage.length ?? -1;
  } catch {
    return { keys: [], complete: false };
  }
  try {
    keyAt = storage.key;
  } catch {
    return { keys: [], complete: false };
  }
  if (
    !Number.isSafeInteger(length) ||
    length < 0 ||
    typeof keyAt !== "function"
  ) {
    return { keys: [], complete: false };
  }

  const keys = new Set<string>();
  for (let index = 0; index < length; index += 1) {
    try {
      const key = keyAt.call(storage, index);
      if (typeof key === "string") keys.add(key);
    } catch {
      return { keys: [...keys], complete: false };
    }
  }
  return { keys: [...keys], complete: true };
}

/**
 * Strict inventory proof for an absent target.  The ordinary generator
 * intentionally tolerates incomplete enumeration and then fails open; this
 * admission path must instead know that it observed every stored source.
 */
function snapshotStrictStorageKeys(
  storage: ClockStorage | null | undefined,
): StorageKeySnapshot {
  if (!storage) return { keys: [], complete: false };
  let length: number;
  let keyAt: Storage["key"] | undefined;
  try {
    length = storage.length ?? -1;
    keyAt = storage.key;
  } catch {
    return { keys: [], complete: false };
  }
  if (
    !Number.isSafeInteger(length) ||
    length < 0 ||
    typeof keyAt !== "function"
  ) {
    return { keys: [], complete: false };
  }

  const keys: string[] = [];
  const seen = new Set<string>();
  for (let index = 0; index < length; index += 1) {
    let key: string | null;
    try {
      key = keyAt.call(storage, index);
    } catch {
      return { keys, complete: false };
    }
    if (typeof key !== "string" || seen.has(key)) {
      return { keys, complete: false };
    }
    seen.add(key);
    keys.push(key);
  }
  try {
    if (storage.length !== length) return { keys, complete: false };
  } catch {
    return { keys, complete: false };
  }
  return { keys, complete: true };
}

function readStorageValue(
  storage: ClockStorage | null | undefined,
  key: string,
): { available: boolean; value: string | null } {
  if (!storage) return { available: false, value: null };
  try {
    const getItem = storage.getItem;
    if (typeof getItem !== "function") {
      return { available: false, value: null };
    }
    return { available: true, value: getItem.call(storage, key) };
  } catch {
    return { available: false, value: null };
  }
}

function writeStorageValue(
  storage: ClockStorage | null | undefined,
  key: string,
  value: string,
): boolean {
  if (!storage) return false;
  try {
    const setItem = storage.setItem;
    if (typeof setItem !== "function") return false;
    setItem.call(storage, key, value);
    return true;
  } catch {
    return false;
  }
}

function removeStorageValue(
  storage: ClockStorage | null | undefined,
  key: string,
): boolean {
  if (!storage) return false;
  try {
    const removeItem = storage.removeItem;
    if (typeof removeItem !== "function") return false;
    removeItem.call(storage, key);
    return true;
  } catch {
    return false;
  }
}

function clockRecordsFromSnapshot(
  storage: ClockStorage | null | undefined,
  snapshot: StorageKeySnapshot,
): Array<CheckoffOperationClockRecordV4 & { key: string }> {
  const records: Array<CheckoffOperationClockRecordV4 & { key: string }> = [];
  for (const key of snapshot.keys) {
    if (!key.startsWith(CHECKOFF_OPERATION_CLOCK_STORAGE_PREFIX_V4)) continue;
    const read = readStorageValue(storage, key);
    if (!read.available) continue;
    const record = parseCheckoffOperationClockRecord(key, read.value);
    if (record) records.push({ ...record, key });
  }
  return records;
}

function compactCheckoffOperationClockRecords(
  storage: ClockStorage | null | undefined,
): void {
  const snapshot = snapshotStorageKeys(storage);
  if (!snapshot.complete) return;
  const records = clockRecordsFromSnapshot(storage, snapshot);
  const maximum = records.reduce<(typeof records)[number] | undefined>(
    (current, record) =>
      !current ||
      compareCheckoffOperationFreshness(record.freshness, current.freshness) > 0
        ? record
        : current,
    undefined,
  );
  if (!maximum) return;

  for (const candidate of records) {
    if (
      candidate.key === maximum.key ||
      compareCheckoffOperationFreshness(
        candidate.freshness,
        maximum.freshness,
      ) >= 0
    ) {
      continue;
    }

    // Both records are immutable: the complete operation ID is encoded in
    // the key and must exactly equal the raw value. Revalidate immediately
    // before deletion. A concurrent operation has a distinct key and cannot
    // be erased by removing this dominated snapshotted record.
    const currentMaximum = readStorageValue(storage, maximum.key);
    const currentCandidate = readStorageValue(storage, candidate.key);
    const parsedMaximum = currentMaximum.available
      ? parseCheckoffOperationClockRecord(maximum.key, currentMaximum.value)
      : undefined;
    const parsedCandidate = currentCandidate.available
      ? parseCheckoffOperationClockRecord(candidate.key, currentCandidate.value)
      : undefined;
    if (
      parsedMaximum?.operationId !== maximum.operationId ||
      parsedCandidate?.operationId !== candidate.operationId ||
      compareCheckoffOperationFreshness(
        parsedCandidate.freshness,
        parsedMaximum.freshness,
      ) >= 0
    ) {
      continue;
    }
    removeStorageValue(storage, candidate.key);
  }
}

function persistCheckoffOperationClockRecord(
  storage: ClockStorage | null | undefined,
  operationId: string,
): boolean {
  const key = checkoffOperationClockRecordStorageKey(operationId);
  if (!writeStorageValue(storage, key, operationId)) return false;
  const readback = readStorageValue(storage, key);
  if (
    !readback.available ||
    readback.value !== operationId ||
    parseCheckoffOperationClockRecord(key, readback.value)?.operationId !==
      operationId
  ) {
    return false;
  }
  compactCheckoffOperationClockRecords(storage);
  return true;
}

interface OperationPreview {
  operationId: string;
  freshness: CheckoffOperationFreshness;
  nextInstanceId: string;
  nextSequence: string;
}

function previewCheckoffOperationId(
  observed: readonly CheckoffOperationFreshness[],
  now: number,
): OperationPreview {
  const physicalTime = Math.max(
    validPhysical(String(now)) ? now : 0,
    ...observed.map((item) => item.physicalTime),
  );
  const sameTime = observed.filter(
    (item) => item.physicalTime === physicalTime,
  );
  const maxLogical = sameTime.reduce<string | undefined>(
    (max, item) =>
      !max || compareCanonicalDecimal(item.logicalTime, max) > 0
        ? item.logicalTime
        : max,
    undefined,
  );
  const logicalTime =
    maxLogical === undefined ? "0" : incrementCanonicalDecimal(maxLogical);
  const nextSequence = incrementCanonicalDecimal(sequence);
  const nextInstanceId =
    instanceId ??
    (typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID().replaceAll("-", "_")
      : `instance-${Math.random().toString(36).slice(2)}`);
  const operationId = `checkoff:v3:${physicalTime}:${logicalTime}:${nextInstanceId}:${nextSequence}`;
  return {
    operationId,
    freshness: parseCheckoffOperationId(operationId)!,
    nextInstanceId,
    nextSequence,
  };
}

function commitOperationPreview(
  storage: ClockStorage | null | undefined,
  preview: OperationPreview,
): boolean {
  instanceId = preview.nextInstanceId;
  sequence = preview.nextSequence;
  processClock = preview.freshness;
  return persistCheckoffOperationClockRecord(storage, preview.operationId);
}

function removeExactClockValue(
  storage: ClockStorage | null | undefined,
  key: string,
  raw: string,
): boolean {
  const first = readStorageValue(storage, key);
  if (!first.available || first.value !== raw) return false;
  const second = readStorageValue(storage, key);
  if (!second.available || second.value !== raw) return false;
  if (!removeStorageValue(storage, key)) return false;
  const after = readStorageValue(storage, key);
  return after.available && after.value === null;
}

interface ClockSourceScan {
  complete: boolean;
  safe: CheckoffOperationFreshness[];
}

interface ReadOnlyClockSourceScan extends ClockSourceScan {
  unsafe: boolean;
}

function scanAndCompactClockSources(
  storage: ClockStorage | null | undefined,
  serverTimeMs: number,
  snapshot: StorageKeySnapshot,
): ClockSourceScan {
  if (!snapshot.complete) return { complete: false, safe: [] };
  const safe: CheckoffOperationFreshness[] = [];
  for (const key of snapshot.keys) {
    if (!key.startsWith(CHECKOFF_OPERATION_CLOCK_STORAGE_PREFIX_V4)) continue;
    const read = readStorageValue(storage, key);
    if (!read.available) return { complete: false, safe };
    if (read.value === null) continue;
    const parsed = parseCheckoffOperationClockRecord(key, read.value);
    if (
      parsed &&
      isGeneratorSafeSource(
        { kind: "v4-observation", operationId: parsed.operationId },
        serverTimeMs,
      )
    ) {
      safe.push(parsed.freshness);
    } else if (!removeExactClockValue(storage, key, read.value)) {
      return { complete: false, safe };
    }
  }
  for (const [key, kind] of [
    [CHECKOFF_OPERATION_CLOCK_STORAGE_KEY_V3, "v3-clock"],
    [LEGACY_CLOCK_KEY, "v1-clock"],
  ] as const) {
    const read = readStorageValue(storage, key);
    if (!read.available) return { complete: false, safe };
    if (read.value === null) continue;
    const source: CheckoffGeneratorSafetySource = {
      kind,
      operationId: read.value,
    };
    if (isGeneratorSafeSource(source, serverTimeMs)) {
      safe.push(parseCheckoffOperationId(read.value)!);
    } else if (!removeExactClockValue(storage, key, read.value)) {
      return { complete: false, safe };
    }
  }
  return { complete: true, safe };
}

/**
 * Reads every persisted operation source without changing it.  This is used
 * when there is no row target whose recovery state could justify repairing a
 * malformed record: absent-target admission must stop rather than compact
 * unsafe evidence away.
 */
function scanReadOnlyClockSources(
  storage: ClockStorage | null | undefined,
  serverTimeMs: number,
  snapshot: StorageKeySnapshot,
): ReadOnlyClockSourceScan {
  if (!snapshot.complete) return { complete: false, safe: [], unsafe: false };
  const safe: CheckoffOperationFreshness[] = [];
  let unsafe = false;
  for (const key of snapshot.keys) {
    if (!key.startsWith(CHECKOFF_OPERATION_CLOCK_STORAGE_PREFIX_V4)) continue;
    const read = readStorageValue(storage, key);
    if (!read.available) return { complete: false, safe, unsafe };
    if (read.value === null) continue;
    const parsed = parseCheckoffOperationClockRecord(key, read.value);
    if (
      !parsed ||
      !isGeneratorSafeSource(
        { kind: "v4-observation", operationId: parsed.operationId },
        serverTimeMs,
      )
    ) {
      unsafe = true;
      continue;
    }
    safe.push(parsed.freshness);
  }
  for (const [key, kind] of [
    [CHECKOFF_OPERATION_CLOCK_STORAGE_KEY_V3, "v3-clock"],
    [LEGACY_CLOCK_KEY, "v1-clock"],
  ] as const) {
    const read = readStorageValue(storage, key);
    if (!read.available) return { complete: false, safe, unsafe };
    if (read.value === null) continue;
    const source: CheckoffGeneratorSafetySource = {
      kind,
      operationId: read.value,
    };
    if (!isGeneratorSafeSource(source, serverTimeMs)) {
      unsafe = true;
      continue;
    }
    safe.push(parseCheckoffOperationId(read.value)!);
  }
  return { complete: true, safe, unsafe };
}

export interface PolicySafeCheckoffOperationInput {
  storage: ClockStorage | null | undefined;
  target: { groceryListId: string; itemId: string };
  baseline: ServerConfirmedCheckoff | undefined;
  baselineItemId: string | undefined;
  authoritativeServerTimeMs: number | undefined;
  observedEntries?: readonly CheckoffOperationLike[];
  observedRetirementOperationIds?: readonly string[];
  ordinaryRetirementOperationIds?: readonly string[];
  recoverySourcesComplete?: boolean;
  recoverySourceHealth?: {
    complete: boolean;
    malformed: boolean;
    unreadable: boolean;
  };
  now?: number;
}

/**
 * Policy-safe operation admission for a prepared create target.  It has no
 * item, baseline, or epoch because the target is honestly absent; callers
 * must supply the server clock from the prepare response instead.
 */
export interface PolicySafeAbsentCheckoffOperationInput {
  storage: ClockStorage | null | undefined;
  authoritativeServerTimeMs: number | undefined;
  observedEntries?: readonly CheckoffOperationLike[];
  observedRetirementOperationIds?: readonly string[];
  now?: number;
}

export interface PolicySafeCheckoffOperationTarget {
  target: { groceryListId: string; itemId: string };
  baseline: ServerConfirmedCheckoff | undefined;
  baselineItemId: string | undefined;
}

export interface PolicySafeCheckoffOperationTargetsInput {
  storage: ClockStorage | null | undefined;
  targets: readonly PolicySafeCheckoffOperationTarget[];
  authoritativeServerTimeMs: number | undefined;
  observedEntries?: readonly CheckoffOperationLike[];
  observedRetirementOperationIds?: readonly string[];
  ordinaryRetirementOperationIds?: readonly string[];
  recoverySourcesComplete?: boolean;
  recoverySourceHealth?: {
    complete: boolean;
    malformed: boolean;
    unreadable: boolean;
  };
  now?: number;
}

export type PolicySafeCheckoffOperationBlocked = {
  kind: "blocked";
  reason:
    | "storage-unreadable"
    | "recovery-pending"
    | "invalid-server-time"
    | "unsafe-clock-source"
    | "unsafe-process-clock"
    | "unsafe-next-operation";
};

export type PolicySafeCheckoffOperationResult =
  | { kind: "created"; operationId: string }
  | PolicySafeCheckoffOperationBlocked;

export type PolicySafeCheckoffOperationTargetsResult =
  | {
      kind: "created";
      operationId: string;
      recoveredMarkers: readonly CheckoffPolicyRecoveryMarkerV1[];
    }
  | PolicySafeCheckoffOperationBlocked;

/**
 * Creates one operation for an honestly absent manual-add target.  Unlike
 * ordinary no-marker generation, this path cannot repair storage because it
 * has no authoritative item boundary to bind that repair to.
 */
export function createPolicySafeAbsentCheckoffOperationId({
  storage,
  authoritativeServerTimeMs,
  observedEntries = [],
  observedRetirementOperationIds = [],
  now = Date.now(),
}: PolicySafeAbsentCheckoffOperationInput): PolicySafeCheckoffOperationResult {
  if (
    !Number.isSafeInteger(authoritativeServerTimeMs) ||
    authoritativeServerTimeMs === undefined ||
    authoritativeServerTimeMs < 0 ||
    !validPhysical(String(authoritativeServerTimeMs))
  ) {
    return { kind: "blocked", reason: "invalid-server-time" };
  }

  const clocks = scanReadOnlyClockSources(
    storage,
    authoritativeServerTimeMs,
    snapshotStrictStorageKeys(storage),
  );
  if (!clocks.complete) {
    return { kind: "blocked", reason: "storage-unreadable" };
  }
  if (clocks.unsafe) {
    return { kind: "blocked", reason: "unsafe-clock-source" };
  }

  const observed = [...clocks.safe];
  for (const entry of observedEntries) {
    if (
      !entry.operationId ||
      !isGeneratorSafeSource(
        { kind: "outbox", operationId: entry.operationId },
        authoritativeServerTimeMs,
      )
    ) {
      return { kind: "blocked", reason: "unsafe-clock-source" };
    }
    observed.push(parseCheckoffOperationId(entry.operationId)!);
  }
  for (const operationId of observedRetirementOperationIds) {
    if (
      !isGeneratorSafeSource(
        { kind: "retirement", operationId },
        authoritativeServerTimeMs,
      )
    ) {
      return { kind: "blocked", reason: "unsafe-clock-source" };
    }
    observed.push(parseCheckoffOperationId(operationId)!);
  }
  if (processClock) {
    const processOperationId = `checkoff:v${processClock.version}:${processClock.physicalTime}:${processClock.logicalTime}:${processClock.instanceId}:${processClock.sequence}`;
    if (
      !isGeneratorSafeSource(
        { kind: "process-maximum", operationId: processOperationId },
        authoritativeServerTimeMs,
      )
    ) {
      return { kind: "blocked", reason: "unsafe-process-clock" };
    }
    observed.push(processClock);
  }

  const preview = previewCheckoffOperationId(
    observed,
    Math.max(authoritativeServerTimeMs, validPhysical(String(now)) ? now : 0),
  );
  if (
    !isGeneratorSafeSource(
      { kind: "process-maximum", operationId: preview.operationId },
      authoritativeServerTimeMs,
    )
  ) {
    return { kind: "blocked", reason: "unsafe-next-operation" };
  }
  if (!commitOperationPreview(storage, preview)) {
    return { kind: "blocked", reason: "storage-unreadable" };
  }
  return { kind: "created", operationId: preview.operationId };
}

interface RecoveredOperationTarget extends PolicySafeCheckoffOperationTarget {
  marker: CheckoffPolicyRecoveryMarkerV1;
  hasDurableMarker: boolean;
}

function preflightRecoveredOperationTarget(
  storage: ClockStorage | null | undefined,
  target: PolicySafeCheckoffOperationTarget,
  authoritativeServerTimeMs: number | undefined,
  recoverySourcesComplete: boolean,
  recoverySourceHealth:
    | PolicySafeCheckoffOperationTargetsInput["recoverySourceHealth"]
    | undefined,
): RecoveredOperationTarget | PolicySafeCheckoffOperationBlocked | undefined {
  const durableMarkerRead = readPolicyRecoveryMarker(storage, target.target.itemId);
  if (durableMarkerRead.kind === "unreadable" || durableMarkerRead.kind === "invalid") {
    return {
      kind: "blocked",
      reason:
        durableMarkerRead.kind === "unreadable"
          ? "storage-unreadable"
          : "recovery-pending",
    };
  }
  const fallbackMarker = loadProcessPolicyRecoveryMarker(target.target.itemId);
  const markerRead = fallbackMarker
    ? { kind: "valid" as const, marker: fallbackMarker }
    : durableMarkerRead;
  if (markerRead.kind === "absent") return undefined;
  if (
    !target.baseline ||
    target.baselineItemId !== target.target.itemId ||
    !Number.isSafeInteger(authoritativeServerTimeMs) ||
    authoritativeServerTimeMs! <= markerRead.marker.serverTimeMs
  ) {
    return { kind: "blocked", reason: "recovery-pending" };
  }
  if (
    !recoverySourcesComplete ||
    (recoverySourceHealth &&
      (!recoverySourceHealth.complete ||
        recoverySourceHealth.malformed ||
        recoverySourceHealth.unreadable))
  ) {
    return { kind: "blocked", reason: "storage-unreadable" };
  }
  return {
    ...target,
    marker: markerRead.marker,
    hasDurableMarker: durableMarkerRead.kind === "valid",
  };
}

function matchesExactDurablePolicyRecoveryMarker(
  storage: ClockStorage | null | undefined,
  marker: CheckoffPolicyRecoveryMarkerV1,
): boolean {
  const current = readPolicyRecoveryMarker(storage, marker.itemId);
  return (
    current.kind === "valid" &&
    JSON.stringify(current.marker) === JSON.stringify(marker)
  );
}

/**
 * Admit one operation shared by every target in a bulk reset. Every target is
 * read and validated before the shared operation clock is committed. Recovery
 * markers intentionally remain intact: bulk callers consume them only after
 * their authoritative server response succeeds.
 */
export function createPolicySafeCheckoffOperationIdForTargets({
  storage,
  targets,
  authoritativeServerTimeMs,
  observedEntries = [],
  observedRetirementOperationIds = [],
  ordinaryRetirementOperationIds = [],
  recoverySourcesComplete = false,
  recoverySourceHealth,
  now = Date.now(),
}: PolicySafeCheckoffOperationTargetsInput): PolicySafeCheckoffOperationTargetsResult {
  const recovered: RecoveredOperationTarget[] = [];
  const targetIdentities = new Set<string>();
  for (const target of targets) {
    const targetIdentity = JSON.stringify([
      target.target.groceryListId,
      target.target.itemId,
    ]);
    if (targetIdentities.has(targetIdentity)) {
      return { kind: "blocked", reason: "recovery-pending" };
    }
    targetIdentities.add(targetIdentity);
    const preflight = preflightRecoveredOperationTarget(
      storage,
      target,
      authoritativeServerTimeMs,
      recoverySourcesComplete,
      recoverySourceHealth,
    );
    if (preflight && "kind" in preflight) return preflight;
    if (preflight) recovered.push(preflight);
  }

  if (recovered.length === 0) {
    return {
      kind: "created",
      operationId: createCheckoffOperationId(
        storage,
        [],
        ordinaryRetirementOperationIds,
        now,
      ),
      recoveredMarkers: [],
    };
  }

  for (const target of recovered) {
    if (!matchesExactProcessPolicyRecoveryMarker(target.marker)) {
      return { kind: "blocked", reason: "recovery-pending" };
    }
  }

  const snapshot = snapshotStorageKeys(storage);
  const clocks = scanAndCompactClockSources(
    storage,
    authoritativeServerTimeMs!,
    snapshot,
  );
  if (!clocks.complete) {
    return { kind: "blocked", reason: "storage-unreadable" };
  }

  const observed = [...clocks.safe];
  let unsafeTargetSourcesRemain = false;
  for (const entry of observedEntries) {
    const operationId = entry.operationId;
    if (
      operationId &&
      isGeneratorSafeSource(
        { kind: "outbox", operationId },
        authoritativeServerTimeMs!,
      )
    ) {
      observed.push(parseCheckoffOperationId(operationId)!);
    } else {
      unsafeTargetSourcesRemain = true;
    }
  }
  for (const operationId of observedRetirementOperationIds) {
    if (
      isGeneratorSafeSource(
        { kind: "retirement", operationId },
        authoritativeServerTimeMs!,
      )
    ) {
      observed.push(parseCheckoffOperationId(operationId)!);
    } else {
      unsafeTargetSourcesRemain = true;
    }
  }
  if (processClock) {
    const processOperationId = `checkoff:v${processClock.version}:${processClock.physicalTime}:${processClock.logicalTime}:${processClock.instanceId}:${processClock.sequence}`;
    if (
      !isGeneratorSafeSource(
        { kind: "process-maximum", operationId: processOperationId },
        authoritativeServerTimeMs!,
      )
    ) {
      return { kind: "blocked", reason: "unsafe-process-clock" };
    }
    observed.push(processClock);
  }
  const preview = previewCheckoffOperationId(observed, now);
  if (
    !isGeneratorSafeSource(
      { kind: "process-maximum", operationId: preview.operationId },
      authoritativeServerTimeMs!,
    )
  ) {
    return { kind: "blocked", reason: "unsafe-next-operation" };
  }
  for (const target of recovered) {
    if (
      shouldRetainPolicyRecoveryMarker(target.marker, {
        baseline: target.baseline,
        baselineItemId: target.baselineItemId,
        authoritativeServerTimeMs,
        unsafeTargetSourcesRemain,
        authoritativeNextOperationId: preview.operationId,
      })
    ) {
      return { kind: "blocked", reason: "recovery-pending" };
    }
  }

  if (!commitOperationPreview(storage, preview)) {
    return { kind: "blocked", reason: "storage-unreadable" };
  }
  for (const target of recovered) {
    if (!matchesExactProcessPolicyRecoveryMarker(target.marker)) {
      return { kind: "blocked", reason: "recovery-pending" };
    }
  }
  for (const target of recovered) {
    if (
      target.hasDurableMarker &&
      !matchesExactDurablePolicyRecoveryMarker(storage, target.marker)
    ) {
      return { kind: "blocked", reason: "recovery-pending" };
    }
  }
  return {
    kind: "created",
    operationId: preview.operationId,
    recoveredMarkers: recovered.map(({ marker }) => ({
      ...marker,
      policyCeiling: { ...marker.policyCeiling },
    })),
  };
}

/**
 * Target-aware direct admission. Durable browser data is only an input; the
 * supplied in-memory confirmed baseline remains the sole server authority.
 */
export function createPolicySafeCheckoffOperationId({
  storage,
  target,
  baseline,
  baselineItemId,
  authoritativeServerTimeMs,
  observedEntries = [],
  observedRetirementOperationIds = [],
  ordinaryRetirementOperationIds = [],
  recoverySourcesComplete = false,
  recoverySourceHealth,
  now = Date.now(),
}: PolicySafeCheckoffOperationInput): PolicySafeCheckoffOperationResult {
  const result = createPolicySafeCheckoffOperationIdForTargets({
    storage,
    targets: [{ target, baseline, baselineItemId }],
    authoritativeServerTimeMs,
    observedEntries,
    observedRetirementOperationIds,
    ordinaryRetirementOperationIds,
    recoverySourcesComplete,
    recoverySourceHealth,
    now,
  });
  if (result.kind === "blocked") return result;

  const marker = result.recoveredMarkers[0];
  if (!marker) {
    return { kind: "created", operationId: result.operationId };
  }
  if (!matchesExactProcessPolicyRecoveryMarker(marker)) {
    return { kind: "blocked", reason: "recovery-pending" };
  }
  const durableMarkerRead = readPolicyRecoveryMarker(storage, marker.itemId);
  if (
    durableMarkerRead.kind === "unreadable" ||
    durableMarkerRead.kind === "invalid"
  ) {
    return { kind: "blocked", reason: "recovery-pending" };
  }
  if (
    durableMarkerRead.kind === "valid" &&
    !clearExactPolicyRecoveryMarker(storage, marker)
  ) {
    return { kind: "blocked", reason: "recovery-pending" };
  }
  if (!clearExactProcessPolicyRecoveryMarker(marker)) {
    return { kind: "blocked", reason: "recovery-pending" };
  }
  return { kind: "created", operationId: result.operationId };
}

export function createCheckoffOperationId(
  storage?: ClockStorage | null,
  observedEntries?: readonly CheckoffOperationLike[],
  now?: number,
): string;
export function createCheckoffOperationId(
  storage: ClockStorage | null | undefined,
  observedEntries: readonly CheckoffOperationLike[],
  observedRetirementOperationIds?: readonly string[],
  now?: number,
): string;
export function createCheckoffOperationId(
  storage: ClockStorage | null | undefined,
  observedEntries: readonly CheckoffOperationLike[] = [],
  observedRetirementOperationIds: readonly string[] | number[] | number = [],
  now = Date.now(),
): string {
  if (typeof observedRetirementOperationIds === "number") {
    now = observedRetirementOperationIds;
    observedRetirementOperationIds = [];
  }
  const observed = observedEntries.map(freshnessOf);
  for (const id of observedRetirementOperationIds as readonly string[]) {
    const parsed = parseCheckoffOperationId(id);
    if (parsed) observed.push(parsed);
  }
  const storageSnapshot = snapshotStorageKeys(storage);
  for (const record of clockRecordsFromSnapshot(storage, storageSnapshot)) {
    observed.push(record.freshness);
  }
  for (const compatibilityKey of [
    CHECKOFF_OPERATION_CLOCK_STORAGE_KEY_V3,
    LEGACY_CLOCK_KEY,
  ]) {
    const stored = readStorageValue(storage, compatibilityKey);
    const parsed = stored.available
      ? parseCheckoffOperationId(stored.value ?? undefined)
      : undefined;
    if (parsed) observed.push(parsed);
  }
  if (processClock) observed.push(processClock);
  const preview = previewCheckoffOperationId(observed, now);
  commitOperationPreview(storage, preview);
  return preview.operationId;
}

export function isCheckoffOperationNewerThan(
  entry: CheckoffOperationLike,
  boundaryOperationId: string,
): boolean {
  const boundary = parseCheckoffOperationId(boundaryOperationId);
  return (
    !boundary ||
    compareCheckoffOperationFreshness(freshnessOf(entry), boundary) > 0
  );
}

export function resetCheckoffOperationClockForTests(): void {
  sequence = "0";
  instanceId = undefined;
  processClock = undefined;
}

/** A server policy rejection invalidates the current realm's wall-clock-derived maximum. */
export function resetCheckoffOperationProcessClock(): void {
  processClock = undefined;
}
